import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Spinner } from 'reactstrap';
import axios from 'axios';
import BetterStandsContext from "../Contexts/Management/BetterStandsContext";
import SahaFormContext from "../Contexts/Management/SahaFormContext";
import { JAVA_BACKEND_API_URL, JAVA_BACKEND_USER_PASS, EXTERNAL_IMAGE_PATH, getAuthHeader } from "../Contexts/BackendConstants";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const UygunsuzlukDetailModal = ({
                                  isOpen,
                                  toggle,
                                  selectedUyumsuzluk,
                                  formTypeId,
                                  activeFairId,
                                  onDelete
                                }) => {
  const { t } = useTranslation("saha");
  // ============= STATES =============
  const [categoryData, setCategoryData] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [javaPhotos, setJavaPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  // ============= CONTEXTS =============
  const { activeFairId: contextFairId } = useContext(BetterStandsContext);
  const { getFormValues, checkOrSave } = useContext(SahaFormContext);
  const fairId = activeFairId || contextFairId;

  // ============= CALLBACKS =============

  const fetchCategories = useCallback(async () => {
    if (!formTypeId || !isOpen) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
          'https://fds.sakagrup.com/Saha/generateDenetim/',
          { form_type_id: formTypeId },
          { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.dataset) {
        setCategoryData(response.data.dataset);
        console.log('✅ Kategoriler yüklendi');
      } else {
        setError(i18n.t("saha:uygunsuzlukDetailModal.formVerisiAlinamadi"));
      }
    } catch (err) {
      console.error('Kategoriler yüklenirken hata:', err);
      setError(i18n.t("saha:uygunsuzlukDetailModal.kategorilerYuklenemedi"));
      setCategoryData(null);
    } finally {
      setLoading(false);
    }
  }, [formTypeId, isOpen]);

  const fetchFormResponses = useCallback(async () => {
    if (!isOpen) return;

    // ✅ Global activeFormId'ye BAKMA — her açılışta sıfırdan taze arama yap
    let resolvedFormId = null;

    const standName = selectedUyumsuzluk?.[6];

    if (!standName || !fairId || !formTypeId) {
      console.warn('⚠️ Stand adı, fairId veya formTypeId eksik — cevaplar yüklenemiyor');
      setFormResponses({});
      return;
    }

    try {
      console.log('🔍 Stand adıyla arama yapılıyor:', standName);

      const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS);
      const token  = localStorage.getItem('token')  || '';
      const userId = localStorage.getItem('userId') || '';

      const standRes = await axios.get(
        `${JAVA_BACKEND_API_URL}/betterStands/standsByFuarIdOpt2/${fairId}`,
        {
          params: { page: 0, limit: 5, salonId: null, search: standName },
          headers: { Authorization: authHeader, 'X-token': token, 'X-user-id': userId },
        }
      );

      const stands = standRes.data?.fullStandInfoDTOS?.content;
      if (!stands || stands.length === 0) {
        console.warn('⚠️ Stand bulunamadı:', standName);
        setFormResponses({});
        return;
      }

      const standId = stands[0].standId;
      console.log('✅ StandId bulundu:', standId);

      const formRes = await checkOrSave(standId, 1, formTypeId, 0, fairId, userId);
      resolvedFormId = formRes?._id || formRes?.id;
      console.log('✅ resolvedFormId:', resolvedFormId);

    } catch (err) {
      console.error('❌ Stand/Form ID bulunamadı:', err.message);
      setFormResponses({});
      return;
    }

    if (!resolvedFormId) {
      console.warn('⚠️ resolvedFormId hâlâ boş');
      setFormResponses({});
      return;
    }

    // ── Form cevaplarını getir ──
    try {
      console.log('📍 getFormValues çağrılıyor, formId:', resolvedFormId);
      const responses = await getFormValues(resolvedFormId);
      console.log('📦 getFormValues response:', responses);

      if (!Array.isArray(responses) || responses.length === 0) {
        console.warn('⚠️ Cevaplar boş');
        setFormResponses({});
      } else {
        const organizedResponses = {};
        responses.forEach(item => {
          if (!organizedResponses[item.categoryId]) {
            organizedResponses[item.categoryId] = {};
          }
          organizedResponses[item.categoryId][item.subId] = item.answerValue;
          console.log(`   ✓ [Cat:${item.categoryId}, Sub:${item.subId}] = ${item.answerValue}`);
        });
        setFormResponses(organizedResponses);
        console.log('✅ Cevaplar yüklendi!');
      }
    } catch (err) {
      console.error('❌ getFormValues HATA:', err.message);
      setFormResponses({});
      setError(i18n.t("saha:uygunsuzlukDetailModal.cevaplarYuklenemedi") + err.message);
    }

    // ── PHP'de fotoğraf yoksa Java/MongoDB'den çek ──
    const phpPhotos = Array.isArray(selectedUyumsuzluk?.[1]) ? selectedUyumsuzluk[1] : [];
    if (phpPhotos.length === 0) {
      try {
        console.log('📸 PHP fotoğrafı bulunamadı, Java endpoint deneniyor... formId:', resolvedFormId);
        setPhotosLoading(true);
        const photoRes = await axios.get(
          `${JAVA_BACKEND_API_URL}/form/sahaform/${resolvedFormId}/photos`,
          { headers: getAuthHeader() }
        );
        const photos = Array.isArray(photoRes.data) ? photoRes.data : [];
        console.log(`📸 Java fotoğrafları: ${photos.length} adet`, photos);
        setJavaPhotos(photos);
      } catch (photoErr) {
        console.error('❌ Java fotoğrafları yüklenemedi:', photoErr.message);
        setJavaPhotos([]);
      } finally {
        setPhotosLoading(false);
      }
    } else {
      console.log(`✅ PHP fotoğrafları mevcut (${phpPhotos.length} adet), Java endpoint atlanıyor`);
    }

  // ✅ activeFormId dependency'si KALDIRILDI — artık global state'e bağımlı değil
  }, [isOpen, selectedUyumsuzluk, formTypeId, fairId, getFormValues, checkOrSave]);

  // ============= EFFECTS =============

  useEffect(() => {
    async function run() {
      if (isOpen) {
        console.log('📂 Modal açıldı');
        console.log('   formTypeId:', formTypeId);
        console.log('   selectedUyumsuzluk:', selectedUyumsuzluk);
        await fetchCategories();
        await fetchFormResponses();
      } else {
        setCategoryData(null);
        setFormResponses({});
        setError(null);
        setJavaPhotos([]);
        setPhotosLoading(false);
      }
    }
    run();
  }, [isOpen, formTypeId, selectedUyumsuzluk, fetchCategories, fetchFormResponses]);

  // ============= HELPERS =============

  const getUygunsuzlukInfo = () => {
    if (!selectedUyumsuzluk) return null;
    // [1] → PHP'den gelen uygunsuzluğa özgü fotoğraf array'i
    const rawPhotos = selectedUyumsuzluk[1];
    return {
      id:       selectedUyumsuzluk[0],
      photos:   Array.isArray(rawPhotos) ? rawPhotos : [],
      formType: selectedUyumsuzluk[2],
      konu:     selectedUyumsuzluk[3],
      tehlike:  selectedUyumsuzluk[4],
      firma:    selectedUyumsuzluk[5],
      stand:    selectedUyumsuzluk[6],
      denetmen: selectedUyumsuzluk[7],
      tarih:    selectedUyumsuzluk[8],
    };
  };

  const uygunsuzlukInfo = getUygunsuzlukInfo();

  // ============= HANDLERS =============

  const handleDelete = async () => {
    if (!uygunsuzlukInfo) return;

    if (window.confirm(t("uygunsuzlukDetailModal.buUygunsuzlukKaydiniSilmekIstediginizden"))) {
      try {
        await axios.delete(
            `https://fds.sakagrup.com/Saha/xDeleteUyumsuzluk/${uygunsuzlukInfo.id}`
        );

        if (onDelete) {
          onDelete();
        }

        toggle();
      } catch (err) {
        console.error('Silme hatası:', err);
        alert(t("uygunsuzlukDetailModal.silmeIslemiBasarisizOldu") + err.message);
      }
    }
  };

  const handlePrint = () => {
    // Modal body içeriğini al
    const modalBodyEl = document.querySelector('.uygunsuzluk-modal-print .modal-body');
    const modalTitleEl = document.querySelector('.uygunsuzluk-modal-print .modal-header span');

    if (!modalBodyEl) {
      console.warn('Modal içeriği bulunamadı');
      return;
    }

    const title = modalTitleEl ? modalTitleEl.innerText : t("uygunsuzlukDetailModal.uygunsuzlukDetayi");
    const bodyHTML = modalBodyEl.innerHTML;

    // Sayfanın mevcut CSS linklerini topla
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(link => `<link rel="stylesheet" href="${link.href}" />`)
      .join('\n');

    // Inline style taglarını topla
    const inlineStyles = Array.from(document.querySelectorAll('style'))
      .map(s => `<style>${s.innerHTML}</style>`)
      .join('\n');

    // Yeni pencere aç
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      alert(t("uygunsuzlukDetailModal.popupEngellendiLutfenTarayicinizinPopup"));
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="tr">
        <head>
          <meta charset="UTF-8" />
          <title>${title}</title>
          ${styleLinks}
          ${inlineStyles}
          <style>
            body {
              font-family: Arial, sans-serif;
              background: white;
              padding: 20px;
              color: #000;
            }
            .print-header {
              border-bottom: 2px solid #333;
              margin-bottom: 16px;
              padding-bottom: 10px;
            }
            .print-header h5 {
              margin: 0;
              font-size: 1.1rem;
              font-weight: bold;
            }
            /* Alert/Spinner gibi bileşenleri gizle (yazdırmada gereksiz) */
            .alert-info { display: none !important; }
            .spinner-border { display: none !important; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h5>${title}</h5>
          </div>
          ${bodyHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Stiller yüklendikten sonra yazdır
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 300);
    };
  };

  // ============= RENDER =============

  return (
      <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="uygunsuzluk-modal-print"
          style={{ maxWidth: '1100px', width: '95vw' }}
      >
        <ModalHeader
            className="d-flex justify-content-between align-items-center"
            style={{ padding: '16px 28px', backgroundColor: '#f1f3f5', borderBottom: '2px solid #dee2e6' }}
        >
          <span>
            {uygunsuzlukInfo ? t("uygunsuzlukDetailModal.uygunsuzlukDetayi2", { formType: uygunsuzlukInfo.formType }) : t("uygunsuzlukDetailModal.uygunsuzlukDetayi")}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
                color="danger"
                size="sm"
                onClick={toggle}
                style={{ marginRight: '5px' }}
            >
              {t("uygunsuzlukDetailModal.geriDon")}
            </Button>
            <Button
                color="success"
                size="sm"
                onClick={handlePrint}
            >
              {t("uygunsuzlukDetailModal.yazdir")}
            </Button>
          </div>
        </ModalHeader>

        <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto', padding: '24px 28px' }}>
          {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
          )}

          {uygunsuzlukInfo && (
              <>
                {/* TEMELBİLGİLER */}
                <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.stand")}</strong> {uygunsuzlukInfo.stand}
                    </div>
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.firma")}</strong> {uygunsuzlukInfo.firma}
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.denetmen")}</strong> {uygunsuzlukInfo.denetmen}
                    </div>
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.tarih")}</strong> {uygunsuzlukInfo.tarih}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.konu")}</strong> {uygunsuzlukInfo.konu}
                    </div>
                    <div className="col-md-6">
                      <strong>{t("uygunsuzlukDetailModal.tehlike")}</strong> {uygunsuzlukInfo.tehlike}
                    </div>
                  </div>
                </div>

                {/* FOTOĞRAFLAR — PHP önce, yoksa Java/MongoDB fallback */}
                {uygunsuzlukInfo.photos.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="mb-2" style={{ fontWeight: 'bold', color: '#333' }}>
                        {t("uygunsuzlukDetailModal.fotograflar")}{" "}<small style={{ color: '#888', fontWeight: 'normal' }}>{t("uygunsuzlukDetailModal.php")}</small>
                      </h6>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {uygunsuzlukInfo.photos.map((photo, index) => (
                            photo.saha_form_photo_link && (
                                <div key={index} style={{ flexBasis: 'calc(50% - 5px)' }}>
                                  <img
                                      src={EXTERNAL_IMAGE_PATH + photo.saha_form_photo_link}
                                      alt={t("uygunsuzlukDetailModal.fotograf", { v: index + 1 })}
                                      style={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: '4px' }}
                                      onError={(e) => {
                                        console.warn('❌ PHP fotoğrafı yüklenemedi:', photo.saha_form_photo_link);
                                        e.target.style.display = 'none';
                                      }}
                                      onLoad={() => console.log('✅ PHP fotoğraf yüklendi:', photo.saha_form_photo_link)}
                                  />
                                </div>
                            )
                        ))}
                      </div>
                    </div>
                ) : photosLoading ? (
                    <div className="mb-4 text-center py-3">
                      <Spinner size="sm" color="secondary" />
                      <span className="ms-2 text-muted">{t("uygunsuzlukDetailModal.fotograflarYukleniyor")}</span>
                    </div>
                ) : javaPhotos.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="mb-2" style={{ fontWeight: 'bold', color: '#333' }}>
                        {t("uygunsuzlukDetailModal.fotograflar")}{" "}<small style={{ color: '#888', fontWeight: 'normal' }}>{t("uygunsuzlukDetailModal.mongoDB")}</small>
                      </h6>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {javaPhotos.map((photo, index) => (
                            photo.storedFileName && (
                                <div key={photo.id || index} style={{ flexBasis: 'calc(50% - 5px)' }}>
                                  <img
                                      src={`https://gw.sakagrup.com:8443/public/files/sahaformphotos/${photo.storedFileName}`}
                                      alt={photo.originalFileName || t("uygunsuzlukDetailModal.fotograf", { v: index + 1 })}
                                      style={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: '4px' }}
                                      onError={(e) => {
                                        console.warn('❌ Java fotoğrafı yüklenemedi:', photo.storedFileName);
                                        e.target.style.display = 'none';
                                      }}
                                      onLoad={() => console.log('✅ Java fotoğraf yüklendi:', photo.storedFileName)}
                                  />
                                </div>
                            )
                        ))}
                      </div>
                    </div>
                ) : null}

                {/* KATEGORİLER */}
                <div className="mt-4">
                  <h6 className="mb-3" style={{ fontWeight: 'bold', color: '#333' }}>
                    {t("uygunsuzlukDetailModal.formKategorileri")}
                  </h6>

                  {loading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2">{t("uygunsuzlukDetailModal.kategorilerYukleniyor")}</p>
                      </div>
                  ) : categoryData && categoryData.length > 0 ? (
                      <div>
                        {categoryData.map((category) => (
                            <div key={category.id} className="mb-4">
                              <div
                                  style={{
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    padding: '10px 12px',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    marginBottom: '8px'
                                  }}
                              >
                                {category.name}
                              </div>

                              <div style={{ marginLeft: '12px', paddingLeft: '12px', borderLeft: '3px solid #ddd' }}>
                                {category.subs && category.subs.length > 0 ? (
                                    category.subs.map((sub) => {
                                      const answerValue = formResponses[category.id]?.[sub.id];

                                      let valueLabel = t("uygunsuzlukDetailModal.cevapYok");
                                      let valueColor = "#6c757d";

                                      if (answerValue !== undefined) {
                                        if (sub.quest_type === "0") {
                                          if (answerValue === 1) {
                                            valueLabel = t("uygunsuzlukDetailModal.var");
                                            valueColor = "#28a745";
                                          } else if (answerValue === 2) {
                                            valueLabel = t("uygunsuzlukDetailModal.yok");
                                            valueColor = "#dc3545";
                                          } else if (answerValue === 0) {
                                            valueLabel = t("uygunsuzlukDetailModal.gerekYok");
                                            valueColor = "#6c757d";
                                          }
                                        } else {
                                          if (answerValue === 1) {
                                            valueLabel = t("uygunsuzlukDetailModal.uygun");
                                            valueColor = "#28a745";
                                          } else if (answerValue === 2) {
                                            valueLabel = t("uygunsuzlukDetailModal.uygunDegil");
                                            valueColor = "#dc3545";
                                          } else if (answerValue === 0) {
                                            valueLabel = t("uygunsuzlukDetailModal.yok_2");
                                            valueColor = "#6c757d";
                                          }
                                        }
                                      }

                                      return (
                                          <div
                                              key={sub.id}
                                              className="d-flex justify-content-between align-items-center mb-2"
                                              style={{
                                                padding: '10px',
                                                backgroundColor: '#ffc107',
                                                borderRadius: '3px'
                                              }}
                                          >
                                <span style={{ color: '#333', fontWeight: '500' }}>
                                  {sub.name}
                                </span>
                                            <span
                                                style={{
                                                  padding: '4px 12px',
                                                  borderRadius: '3px',
                                                  backgroundColor: valueColor,
                                                  color: '#fff',
                                                  fontSize: '0.85rem',
                                                  fontWeight: 'bold'
                                                }}
                                            >
                                  {valueLabel}
                                </span>
                                          </div>
                                      );
                                    })
                                ) : (
                                    <p className="text-muted mb-0">{t("uygunsuzlukDetailModal.altKategoriYok")}</p>
                                )}
                              </div>
                            </div>
                        ))}
                      </div>
                  ) : (
                      !loading && (
                          <Alert color="info" className="mb-0">
                            {t("uygunsuzlukDetailModal.kategoriVerisiAlinamadi")}
                          </Alert>
                      )
                  )}
                </div>
              </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
              color="danger"
              onClick={handleDelete}
              disabled={loading}
          >
            {t("common:sil")}
          </Button>
          <Button
              color="secondary"
              onClick={toggle}
              disabled={loading}
          >
            {t("common:kapat")}
          </Button>
        </ModalFooter>
      </Modal>
  );
};

export default UygunsuzlukDetailModal;

